import { useCallback, useEffect } from 'react';
import { get, remove, set } from '@analytics/global-storage-utils';
import { Analytics, AnalyticsInstance } from 'analytics';

import { historyReplaceState, useLocation } from '@oaknorth/util-routing';
import { getLogger } from '@oaknorth/util-rum';
import { User, useUserInfo } from '@oaknorth/util-user';

import { pendoAnalytics } from '../pendo';
import { createEventName, getPageName, shouldTrack } from '../utils';

import { AnalyticsActionType } from './types';

const APP_NAME = 'OakNorth Enterprise';

// TODO: move to env variable
const PENDO_ID = '4d64eee3-0180-4104-4952-ed8281ef79da';

const ONCI_EMAIL_DOMAIL = '@onci.com';

// only debug on localhost or dev environments
const debugging = process.env.NODE_ENV !== 'production' || window.location.href.includes('.dev.oaknorth.com');

let analytics: AnalyticsInstance | undefined;

const trackPageView = (user: User) => {
  if (analytics) {
    // eslint-disable-next-line i18next/no-literal-string -- debugging
    debugging && console.debug('tracking :: page view');

    const logger = getLogger();
    logger.log(`Tracking: page view for ${user.email} (${user.userId}) on tenant ${user.tenantId}`);

    analytics.page();
  } else {
    // eslint-disable-next-line i18next/no-literal-string -- debugging
    debugging && console.debug('tracking :: do nothing :: not initialized:: page view');
  }
};

export function useInitAnalytics(enabled = true) {
  const user = useUserInfo();
  const { initAnalytics } = useAnalytics();

  useEffect(() => {
    if (user && enabled) {
      initAnalytics(user);
    }
  }, [user, initAnalytics, enabled]);
}

export const useTrackPageViews = () => {
  const location = useLocation();
  const user = useUserInfo();
  const { trackPageView } = useAnalytics();

  // When location changes in app send page view
  useEffect(() => {
    if (user) {
      trackPageView(user);
    }
  }, [trackPageView, location, user]);
};

// Exported for testing
export function removeCampaignParameters(url: URL) {
  const campaignParams = Array.from(url.searchParams.keys()).filter((key) => key.startsWith('utm_'));
  if (campaignParams.length > 0) {
    campaignParams.forEach((key) => url.searchParams.delete(key));
    historyReplaceState(null, '', url.href);
  }
}

function initAnalytics(user: User) {
  const location = new URL(window.location.href);
  if (user) {
    if (!analytics && shouldTrack(user)) {
      const logger = getLogger();

      logger.log(`Initialising analytics for ${user.email} (${user.userId}) on tenant ${user.tenantId}`);

      analytics = Analytics({
        app: APP_NAME,
        version: __APP_VERSION__,
        plugins: [
          pendoAnalytics({
            apiKey: PENDO_ID,
            excludeTitle: true,
            excludeAllText: true,
            disableCookies: true,
            disablePersistence: true,
            visitor: {
              id: user.userId,
              tenantId: user.tenantId,
              isOaknorth: user.isONUser,
              hasOnciEmail: user.email.includes(ONCI_EMAIL_DOMAIL),
            },
            account: {
              /*
              the account id will be the tenantId of each user to separate them
              by "client"
              */
              id: user.tenantId,
            },
          }),
        ],

        // Undocumented feature - https://github.com/DavidWells/analytics/issues/129#issuecomment-749782836
        // Prevents analystics wrapper writing to localstorage
        // @ts-expect-error -- undocumented feature missing in typedefs, but suggested by developer
        storage: { getItem: get, setItem: set, removeItem: remove },
      });
      analytics.ready(() => {
        removeCampaignParameters(location);
      });

      // identify the user without exposing information
      analytics.identify(user.userId, {
        tenantId: user.tenantId,
        isOaknorth: user.isONUser,
      });

      trackPageView(user);
    } else {
      removeCampaignParameters(location);
    }
  }
}

export const useAnalytics = (): {
  initAnalytics: (user: User) => void;
  trackPageView: (user: User) => void;
} => {
  return {
    initAnalytics,
    trackPageView,
  };
};
interface IEventAttributes {
  pageName: string;
  elementType: AnalyticsActionType;
  elementId: string;
}

interface IAttributes {
  [key: string]: unknown;
}

/**
 * Track custom events
 * @param event object with the following attributes: "element", "action" and "pageName"
 * @param attrs extra information for that "event"
 */
const trackEvent = ({ pageName, elementType, elementId }: IEventAttributes, attrs?: IAttributes) => {
  try {
    const eventName = createEventName(pageName, elementType, elementId);

    if (analytics) {
      // eslint-disable-next-line @oaknorth/ui/no-template-literals -- debugging
      debugging && console.debug(`tracking :: event ${eventName} with attrs = `, attrs);
      analytics.track(eventName, attrs);
    } else {
      debugging &&
        // eslint-disable-next-line @oaknorth/ui/no-template-literals -- debugging
        console.debug(`tracking :: do nothing :: not initialized :: track event ${eventName} with attrs = `, attrs);
    }
  } catch (error) {
    console.error(error);
  }
};

export interface TrackEvent {
  /**
   * Track event manually
   */
  trackEvent: (event: Omit<IEventAttributes, 'pageName'>, attrs?: IAttributes) => void;
  /**
   * Track event inside React "onXX" events like "onClick". To prevent inline functions.
   */
  onTrackEvent: (event: Omit<IEventAttributes, 'pageName'>, attrs?: IAttributes) => () => void;
}

function useTrackEventAnalytics(): TrackEvent {
  const location = useLocation();
  const user = useUserInfo();

  const fireEvent = useCallback(
    (event: Omit<IEventAttributes, 'pageName'>, attrs?: IAttributes | undefined) => {
      const pageName = getPageName(location.pathname);

      trackEvent(
        {
          ...event,
          pageName,
        },
        { ...attrs, userId: user?.userId, tenantId: user?.tenantId }
      );
    },
    [location, user]
  );

  const onTrackEvent = useCallback(
    (event: Omit<IEventAttributes, 'pageName'>, attrs?: IAttributes) => {
      return () => fireEvent(event, attrs);
    },
    [fireEvent]
  );

  return {
    trackEvent: fireEvent,
    onTrackEvent,
  };
}

function useTrackEventMock(): TrackEvent {
  return {
    trackEvent: () => null,
    onTrackEvent: () => () => null,
  };
}

/**
 * We are using this hook everywhere, and because it is relaying on useLocation(),
 * each test should mock that library, so for testing it is always going into a basic URL
 */
export const useTrackEvent = process.env.NODE_ENV === 'test' ? useTrackEventMock : useTrackEventAnalytics;
